import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

/**
 * Přístupný listbox dropdown (Fáze 2). Malý, do jednoho řádku, sedne do
 * `.service-bar` na tmavém/zeleném podkladu. Ovládá se myší i klávesnicí
 * (Šipky/Home/End/Enter/Space/Esc), zavírá se na Esc, blur i klik mimo.
 *
 * Používá vzor `aria-activedescendant`: tlačítko otevře seznam, fokus jde na
 * `role="listbox"` a aktivní položku sleduje `aria-activedescendant`.
 */
export interface DropdownOption<T extends string> {
  readonly value: T;
  /** Plný, čitelný název (accessible label). */
  readonly label: string;
  /** Kompaktní text na tlačítku i v položce (fallback = label). */
  readonly short?: string;
  /** Volitelná ikona (vlajka jazyka, symbol měny) vlevo od textu. */
  readonly icon?: ReactNode;
}

interface DropdownProps<T extends string> {
  readonly value: T;
  readonly options: readonly DropdownOption<T>[];
  onChange(value: T): void;
  readonly ariaLabel: string;
  readonly className?: string;
}

export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const [activeIndex, setActiveIndex] = useState(selectedIndex);

  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const baseId = useId();
  const listId = `${baseId}-list`;
  const optionId = (index: number) => `${baseId}-opt-${index}`;

  const selected = options[selectedIndex] ?? options[0];
  const display = (option: DropdownOption<T>) => option.short ?? option.label;

  const openList = useCallback(() => {
    setActiveIndex(selectedIndex);
    setOpen(true);
  }, [selectedIndex]);

  const closeList = useCallback((focusButton: boolean) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  const commit = useCallback(
    (index: number) => {
      const option = options[index];
      if (option) onChange(option.value);
      closeList(true);
    },
    [options, onChange, closeList],
  );

  // Po otevření přesuň fokus na seznam (aby fungovala klávesnice a blur).
  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  // Zavření klikem mimo komponentu.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const onButtonKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        event.preventDefault();
        openList();
        break;
      default:
        break;
    }
  };

  const onListKeyDown = (event: ReactKeyboardEvent<HTMLUListElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % options.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => (index - 1 + options.length) % options.length);
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        closeList(true);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  // Zavření při odchodu fokusu mimo komponentu (blur).
  const onBlur = (event: ReactFocusEvent<HTMLDivElement>) => {
    if (!rootRef.current?.contains(event.relatedTarget as Node)) {
      setOpen(false);
    }
  };

  if (!selected) return null;

  return (
    <div
      className={className ? `dropdown ${className}` : 'dropdown'}
      ref={rootRef}
      onBlur={onBlur}
    >
      <button
        type="button"
        className="dropdown-toggle"
        ref={buttonRef}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${ariaLabel}: ${selected.label}`}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onButtonKeyDown}
      >
        {selected.icon ? (
          <span className="dropdown-icon" aria-hidden="true">
            {selected.icon}
          </span>
        ) : null}
        <span className="dropdown-value">{display(selected)}</span>
        <span className="dropdown-caret" aria-hidden="true" />
      </button>

      {open && (
        <ul
          className="dropdown-list"
          ref={listRef}
          role="listbox"
          id={listId}
          tabIndex={-1}
          aria-label={ariaLabel}
          aria-activedescendant={optionId(activeIndex)}
          onKeyDown={onListKeyDown}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              id={optionId(index)}
              role="option"
              aria-selected={option.value === value}
              className={
                index === activeIndex ? 'dropdown-option is-active' : 'dropdown-option'
              }
              onMouseEnter={() => setActiveIndex(index)}
              /* Bez preventDefault by mousedown na (nefokusovatelné) položce
                 sebral fokus ze seznamu → onBlur by ho zavřel dřív, než klik
                 stihne vybrat. Proto se výběr „nedal kliknout". */
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => commit(index)}
            >
              {option.icon ? (
                <span className="dropdown-icon" aria-hidden="true">
                  {option.icon}
                </span>
              ) : null}
              <span className="dropdown-option-label">{display(option)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
